/**
 * Сжатие чека перед отправкой в amo Drive.
 *
 * Инструктор фотографирует экран телефона: 3–8 МБ на скриншот, десяток чеков в день — и диск
 * аккаунта amo кончается за недели, а загрузка по мобильной сети рвётся на середине. Ширины
 * 1600 px и качества 0.72 достаточно, чтобы прочитать сумму и время перевода, а это всё, зачем
 * чек нужен.
 *
 * Любая осечка кодека возвращает исходный файл: потерять доказательство оплаты хуже, чем
 * загрузить лишние байты, — по той же причине неработающий курс не блокирует приём денег.
 */

export interface CompressOptions {
  /** Длинная сторона после сжатия, px. */
  readonly maxEdge?: number;
  /** Качество JPEG от 0 до 1. */
  readonly quality?: number;
  /** Файлы меньше этого размера не трогаем — сжимать нечего. */
  readonly skipUnderBytes?: number;
}

const DEFAULTS = {
  maxEdge: 1600,
  quality: 0.72,
  skipUnderBytes: 200 * 1024,
} as const;

function jpegName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base || "receipt"}.jpg`;
}

export async function compressProofImage(file: File, options: CompressOptions = {}): Promise<File> {
  const maxEdge = options.maxEdge ?? DEFAULTS.maxEdge;
  const quality = options.quality ?? DEFAULTS.quality;
  const skipUnderBytes = options.skipUnderBytes ?? DEFAULTS.skipUnderBytes;

  // PDF остаётся PDF: пересобирать его в картинку — терять текстовый слой.
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= skipUnderBytes) return file;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), "image/jpeg", quality);
    });
    // Сжатие, которое не уменьшило файл, — только потеря качества.
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], jpegName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap?.close?.();
  }
}
